<template>
  <b-modal
    centered
    hide-footer
    no-close-on-esc
    no-close-on-backdrop
    v-model="show"
    @close="closeModal"
    class="pa-4"
    :style="'width: 708px !important;'"
  >
    <template #modal-title>
      <h3 class="font-weight-bold">Send Request</h3>
    </template>
    <b-card
      no-body
      v-cloak
      class="rounded-0 border-0 mx-0 bg-light"
      @drop.prevent=""
      @dragover.prevent
    >
      <b-form @submit.prevent="sendRequest">
        <b-card-text :style="'opacity: 1;'">
          <h6 class="font-weight-bold">
            Add an email to share your file
          </h6>
          <b-form-tags
            v-model="form.emails"
            no-outer-focus
            tag-pills
            class="h-auto mb-2 form-control"
          >
            <template
              v-slot="{
                tags,
                inputAttrs,
                inputHandlers,
                disabled,
                addTag,
                removeTag
              }"
            >
              <div class="mb-0">
                <div
                  class="mt-3"
                  style="font-size: 1.5rem;"
                  :class="{
                    'd-inline-block': form.emails.length,
                    'd-none': !form.emails.length
                  }"
                >
                  <b-form-tag
                    class="mr-1 mw-100 badge d-inline-flex align-items-baseline badge-pill border-gray"
                    v-for="(tag, i) in tags"
                    :key="i"
                    :title="tag"
                    :tag-pills="true"
                    :variant="'light'"
                    :disabled="loading"
                    :class="{
                      'border-danger':
                        checkInvalidTag(tag) ||
                        checkExcessTag(tag) ||
                        form.$hasError(`emails.${i}`)
                    }"
                    @remove="removeTag(tag)"
                  >
                    {{ tag }}
                  </b-form-tag>
                </div>
                <b-form-input
                  v-bind="inputAttrs"
                  v-on="inputHandlers"
                  autocomplete="on"
                  placeholder="Enter email..."
                  aria-describedby="sendFeedback"
                  class="border-0 p-0 mb-0"
                  :disabled="disabled || loading"
                  @keydown.enter.native="addTag()"
                ></b-form-input>
              </div>
            </template>
          </b-form-tags>
          <b-form-invalid-feedback
            v-show="tagErrors.message"
            id="sendFeedback"
            :class="{ 'd-block': tagErrors.message }"
          >
            {{ tagErrors.message }}
          </b-form-invalid-feedback>
          <b-form-invalid-feedback
            v-show="form.$getError('emails')"
            id="sendFeedback"
            :class="{ 'd-block': form.$getError('emails') }"
          >
            {{ form.$getError('emails') }}
          </b-form-invalid-feedback>
        </b-card-text>

        <div class="ml-2">
          <b-icon class="mt-2" icon="exclamation-circle" variant="dark" />
          <span class="ml-1 mb-3">
            Press Enter on your keyboard to add an email address
          </span>
        </div>

        <div v-if="template">
          <h3 class="font-weight-bold mt-4">Add due date</h3>
          <b-form-input
            id="input-date"
            type="text"
            required
            placeholder="DD/MM/YYYY"
            aria-describedby="input-date-feedback"
            maxlength="10"
            v-model="form.due_date"
            :formatter="formatDate"
            @input="onSelectedDate"
          ></b-form-input>
          <span class="invalid-feedback d-block">
            {{ dateInvalid }}
          </span>

          <h3 class="font-weight-bold mt-4">Add instruction</h3>
          <b-form-textarea
            id="input-title"
            type="text"
            v-model="form.instruction"
            required
            placeholder="Instructions"
            class="font-weight-normal mb-2"
            rows="5"
          ></b-form-textarea>
        </div>
        <div class="my-4 mx-0">
          <b-container>
            <b-row>
              <b-col cols="12" md="5" class="mb-md-4 mb-3">
                <b-button
                  class="w-100 btn-outlined bg-white text-dark"
                  variant="outline-secondary"
                  :style="'height: 64px !important;'"
                  :disabled="loading"
                  @click="clear"
                >
                  Clear
                </b-button>
              </b-col>
              <b-col cols="12" md="2" class="mb-0"></b-col>
              <b-col cols="12" md="5" class="mb-md-4 mb-3">
                <b-button
                  type="submit"
                  class="w-100 h-auto float-right py-3"
                  variant="primary"
                  :style="'height: 64px !important;'"
                  :loading="loading"
                  :disabled="
                    loading || !form.emails.length || dateInvalid != null
                  "
                >
                  Send
                </b-button>
              </b-col>
            </b-row>
          </b-container>
        </div>
      </b-form>
    </b-card>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex'
import Form from '@/utils/form'
import { isNil } from 'lodash'

export default {
  name: 'SendRequestModal',

  props: {
    showModal: {
      type: Boolean,
      default: false
    },

    request: {
      type: Object,
      default: null
    },

    template: {
      type: Object,
      default: null
    },

    formType: {
      type: String,
      default: 'add'
    }
  },

  data() {
    return {
      loading: false,
      form: new Form({
        name: '',
        project: null,
        uploadType: null,
        due_date: null,
        instruction: null,
        template_id: null,
        information_types: null,
        emails: []
      }),
      validTags: [],
      invalidTags: [],
      maxTags: [],
      tagErrors: {
        message: null,
        error_code: null
      },
      search: '',
      selectedInfoTypes: [],
      dateInvalid: null
    }
  },

  computed: {
    ...mapState({
      user: state => state.auth.user,
      fileRequest: state => state.requests.request
    }),
    isTradieUser() {
      return this.user ? this.user.user_type === 'tradie' : false
    },
    userSubcEmailLimit() {
      let limit = 0

      if (this.isTradieUser) {
        if (this.user.is_subscribed) {
          limit = 5
        } else {
          limit = 1
        }
      } else {
        if (this.user.is_subscribed) {
          limit = 50
        } else {
          limit = 5
        }
      }

      return limit
    },
    show: {
      get() {
        return this.showModal
      },
      set() {
        this.$emit('closeModal')
      }
    }
  },

  methods: {
    closeModal() {
      this.clear()
      this.$emit('closeModal')
    },
    clearSendRequestEmails() {
      this.form.$reset()
    },
    clearSendRequestErrrorEmails() {
      this.invalidTags = []
      this.maxTags = []
      this.tagErrors = {
        message: null,
        error_code: null
      }
    },
    clear() {
      this.clearSendRequestEmails()
      this.clearSendRequestErrrorEmails()
    },
    checkInvalidTag(tag) {
      return this.invalidTags.includes(tag)
    },
    checkExcessTag(tag) {
      return this.maxTags.includes(tag)
    },

    async sendRequest() {
      this.loading = true
      this.form.$busy = true

      if (!this.template) {
        this.form.name = this.request.name
        this.form.project = this.request.project
        this.form.uploadType = this.request.uploadType
        this.form.due_date = this.request.due_date
        this.form.instruction = this.request.instruction
        this.form.information_types = this.request.information_types
      } else {
        this.form.project = undefined
        this.form.name = this.template.name
        this.form.template_id = this.template.id
      }
      this.form.due_date = this.convertToISO(this.form.due_date)

      if (this.formType === 'edit') {
        await this.$api
          .put(`file-request/${this.fileRequest.id}`, this.form.$data())
          .then(res => {
            if (!isNil(res.data.data.invalid)) {
              if (res.data.data.invalid.length) {
                this.invalidTags = res.data.data.invalid
                this.tagErrors.message =
                  'Unable to share files to the highlighted email addresses. They are not registered Business Admin Users on our platform. Please remove to proceed'
              }
            }

            if (isNil(res.data.data.invalid)) {
              this.$emit('sendRequestResponse', res.data)
              this.clear()
              this.closeModal()
            }
          })
          .catch(error => {
            this.form.$handleError(error)

            if (!isNil(error.response.data)) {
              this.tagErrors.error_code = error.response.data.error_code
              this.tagErrors.message = error.response.data.message
            }
          })
          .finally(() => {
            this.loading = false
            this.form.$busy = false
          })
      } else {
        await this.$api
          .post('file-request', this.form.$data())
          .then(res => {
            if (res.data.data.invalid.length) {
              this.invalidTags = res.data.data.invalid
              this.tagErrors.message =
                'Unable to share files to the highlighted email addresses. They are not registered Business Admin Users on our platform. Please remove to proceed'
            }

            // if (res.data.data.max.length) {
            //   this.maxTags = res.data.data.max
            //   this.tagErrors.message =
            //     'Max file sharing capacity or Max Business Admin email address share limit'
            // }

            // if (!res.data.data.invalid.length && !res.data.data.max.length) {
            //   this.$emit('sendRequestResponse', res)
            //   this.clear()
            //   this.closeModal()
            // }

            if (!res.data.data.invalid.length) {
              this.$emit('sendRequestResponse', res.data)
              this.clear()
              this.closeModal()
            }
          })
          .catch(error => {
            this.form.$handleError(error)

            this.tagErrors.error_code = error.response.data.error_code
            if (error.response.data.message.includes('must be a valid email')) {
              this.tagErrors.message =
                'The highlighted emails must be valid email addresses. Please remove to proceed'
            } else {
              this.tagErrors.message = error.response.data.message
            }
          })
          .finally(() => {
            this.loading = false
            this.form.$busy = false
          })
      }
    },

    onSelectedDate(date) {
      if (date.length === 10) {
        this.dateInvalid = null
        this.selectedDate = this.convertToISO(date)
      } else {
        this.dateInvalid = 'Please input a valid date'
      }
    },

    setEditFormEmails() {
      if (this.formType === 'edit') {
        this.form.emails = this.form.emails.concat(this.request.emails)
      }
    }
  },

  watch: {
    show(newVal, oldVal) {
      if (newVal) {
        this.setEditFormEmails()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.badge-secondary {
  color: #364052;
  background: #e8eaed;
}

/deep/ .border-gray {
  border-color: #e5e5e5;
}

/deep/ .b-form-tags {
  overflow: auto;
}

/deep/ .b-form-tags-button {
  display: none;
}
</style>
